import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import "../Pages/CreateEditPage.css";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import "./ModalForm.css";
import { InputTextarea } from "primereact/inputtextarea";
import { FileData, Task } from "../lib/interface";
import { FaCalendarAlt, FaTrashAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Redux/Store/index.ts";
import { Dropdown } from "primereact/dropdown";
import { allPost, allPut, getFiles } from "../Redux/Actions.tsx";
import { urltask } from "../lib/api.ts";
import { useAlert } from "./Alert.tsx";
import DeleteModal from "./DeleteModal.tsx";

import DatePicker, { registerLocale } from "react-datepicker";
import { it } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

// Registra la localizzazione italiana
registerLocale("it", it);

interface ModalFormTaskSingoloProps {
  taskModal: Task;
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  type: string; // "cantiere" | "evento" o simile
  iscreate: boolean; // indica se è in creazione o modifica
}

const ModalFormTaskSingolo: React.FC<ModalFormTaskSingoloProps> = ({
  taskModal,
  isModalOpen,
  setModalOpen,
  type,
  iscreate,
}) => {
  const [task, setTask] = useState<Task>({
    id: "",
    titolo: "",
    descrizione: "",
    dataInizio: "",
    scadenza: "",
    dataFineEffettiva: "",
    priorita: "",
    promemoria: "",
    dataPromemoriaPersonalizzata: "",
    cantiereId: "",
    contrattoQuadroId: "",
    eventoId: "",
    assegnatoA: "",
    stato: "",
  });

  // Stato errors per i 3 campi obbligatori
  const [errors, setErrors] = useState({
    titolo: false,
  });

  const [searchTextDipendente, setSearchTextDipendente] = useState("");
  const [searchTextStato, setSearchTextStato] = useState("");
  const [deleteToTask, setDeleteToTask] = useState<Task | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [isPromemoriaModalOpen, setPromemoriaModalOpen] = useState(false);
  const [switchPromemoria, setSwitchPromemoria] = useState<boolean>(false);
  const [datePickerVisible, setDatePickerVisible] = useState(false);

  const dipendenti = useSelector((state: RootState) => state.utenti.utenti || []);
  const { cantieri } = useSelector((state: RootState) => state.cantieri);
  const { eventi } = useSelector((state: RootState) => state.eventi);

  const dispatch: AppDispatch = useDispatch();
  const { token } = useSelector((state: RootState) => state.login);
  const showAlert = useAlert();

  // Se è già presente un promemoria, abilita lo switch
  useEffect(() => {
    if (task.promemoria) {
      setSwitchPromemoria(true);
    }
  }, [task]);

  // Aggiorna lo stato `task` appena cambia `taskModal`
  useEffect(() => {
    if (isModalOpen) {
      setTask(taskModal);
      setErrors({
        titolo: false,
      });
    }
  }, [taskModal, isModalOpen]);

  // Chiude la modale e resetta campi
  const handleCloseModal = () => {
    setModalOpen(false);
    setSwitchPromemoria(false);
    setTask({
      id: "",
      titolo: "",
      descrizione: "",
      dataInizio: "",
      scadenza: "",
      dataFineEffettiva: "",
      priorita: "",
      promemoria: "",
      dataPromemoriaPersonalizzata: "",
      cantiereId: "",
      contrattoQuadroId: "",
      eventoId: "",
      assegnatoA: "",
      stato: "",
    });
    setPromemoriaModalOpen(false);
    setDatePickerVisible(false);
    setErrors({
      titolo: false,
    });
  };

  // Converte la data in stringa "YYYY-MM-DD"
  const dateToString = (date: Date) => {
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const adjustedDate = new Date(date.getTime() - userTimezoneOffset);
    return adjustedDate.toISOString().split("T")[0];
  };

  const handleDateChange = (name: keyof Task, value: Date | null) => {
    setTask((prev) => ({
      ...prev,
      [name]: value ? dateToString(value) : "",
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox" && name === "switchPromemoria") {
      const checked = (e.target as HTMLInputElement).checked;
      setSwitchPromemoria(checked);
      if (checked) {
        setPromemoriaModalOpen(true);
      } else {
        setPromemoriaModalOpen(false);
        setTask((prev) => ({
          ...prev,
          promemoria: "",
          dataPromemoriaPersonalizzata: "",
        }));
      }
    } else {
      setTask((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Apertura modale di eliminazione
  const deleteTask = (taskToDelete: Task) => {
    handleOpenDeleteModal(taskToDelete);
    handleCloseModal();
  };

  const handleCloseDeleteModal = () => {
    setDeleteToTask(null);
    setIsDeleteModalOpen(false);
  };

  const handleOpenDeleteModal = (taskToDelete: Task) => {
    setDeleteToTask(taskToDelete);
    setIsDeleteModalOpen(true);
  };

  const handlePromemoriaOptionSelect = (option: string) => {
    if (option !== "scelta_libera") {
      setTask((prev) => ({
        ...prev,
        promemoria: option,
      }));
      setPromemoriaModalOpen(false);
    } else {
      // scatta la "data personalizzata"
      setTask((prev) => ({
        ...prev,
        promemoria: "scelta_libera",
      }));
    }
  };

  const handlePromemoriaModalClose = () => {
    setPromemoriaModalOpen(false);
    // Se chiudiamo la modale senza scegliere, disattiva promemoria
    setTask((prev) => ({
      ...prev,
      promemoria: "",
    }));
  };

  // Validazione dei 3 campi obbligatori: titolo, stato, scadenza
  const validateFields = () => {
    const newErrors = {
      titolo: !task.titolo.trim(),
    };
    setErrors(newErrors);
    // Ritorna true solo se TUTTI i campi obbligatori sono ok
    return !Object.values(newErrors).some((val) => val);
  };

  // Salva (crea/modifica)
  const handleSubmit = () => {
    // Validazione
    if (!validateFields()) {
      showAlert(
        "danger",
        "Errore",
        "Compila i campi obbligatori (titolo, stato, scadenza, data inizio).",
        "bottom-end",
        3000
      );
      return; // Non chiudere la modale
    }

    let adjustedTask = { ...task };

    // Pulizia date
    if (adjustedTask.dataInizio) adjustedTask.dataInizio = adjustedTask.dataInizio.split("T")[0].trim();
    if (adjustedTask.scadenza) adjustedTask.scadenza = adjustedTask.scadenza.split("T")[0].trim();
    if (adjustedTask.dataFineEffettiva)
      adjustedTask.dataFineEffettiva = adjustedTask.dataFineEffettiva.split("T")[0].trim();

    // Gestione promemoria
    if (switchPromemoria) {
      if (adjustedTask.promemoria === "UN_GIORNO_PRIMA" || adjustedTask.promemoria === "TRE_GIORNI_PRIMA") {
        if (adjustedTask.scadenza) {
          const scadenzaDate = new Date(adjustedTask.scadenza);
          const daysBefore = adjustedTask.promemoria === "UN_GIORNO_PRIMA" ? 1 : 3;
          const promemoriaDate = new Date(scadenzaDate);
          promemoriaDate.setDate(scadenzaDate.getDate() - daysBefore);
          adjustedTask.dataPromemoriaPersonalizzata = promemoriaDate.toISOString().split("T")[0];
        }
      } else if (adjustedTask.promemoria === "TUTTI_I_GIORNI") {
        adjustedTask.dataPromemoriaPersonalizzata = "";
      } else if (adjustedTask.promemoria === "scelta_libera") {
        if (!adjustedTask.dataPromemoriaPersonalizzata) {
          alert("Seleziona una data per il promemoria personalizzato.");
          return;
        }
      } else {
        // Nessuna scelta => errore
        alert("Seleziona un'opzione per il promemoria.");
        return;
      }
    } else {
      adjustedTask.promemoria = "";
      adjustedTask.dataPromemoriaPersonalizzata = "";
    }

    // Se non specificato, default "UN_GIORNO_PRIMA"
    if (!adjustedTask.promemoria) {
      adjustedTask.promemoria = "UN_GIORNO_PRIMA";
    }

    if (!task.id) {
      // Creazione
      dispatch(allPost(urltask, token, adjustedTask, showAlert)).then((idTask) => {
        if (idTask) {
          showAlert("success", "Creato", "Task creato con successo.", "bottom-end", 3000);
        } else {
          console.error("Creazione del task fallita");
        }
      });
    } else {
      // Modifica
      dispatch(allPut(`${urltask}/${task.id}`, token, adjustedTask, showAlert)).then((updatedTaskRes) => {
        if (updatedTaskRes) {
          showAlert("success", "Salvato", "Task modificato con successo.", "bottom-end", 3000);
        } else {
          console.error("Modifica del task fallita");
        }
      });
    }
    handleCloseModal();
  };

  return (
    <>
      <Dialog
        visible={isModalOpen}
        header={
          <>
            <h6>
              <b>
                {iscreate
                  ? "Crea Nuovo Task"
                  : task.cantiereId
                  ? "Modifica task associato al cantiere"
                  : task.eventoId
                  ? "Modifica task associato all'evento"
                  : "Modifica Task"}
              </b>
            </h6>
            <hr />
          </>
        }
        style={{ width: "50vw", height: "90vh", borderRadius: "24px" }}
        className="custom-dialog"
        onHide={handleCloseModal}
      >
        <div className="modal-content" style={{ borderRadius: "24px" }}>
          {type === "cantiere" ? (
            <Form.Group className="mb-3">
              <Form.Label htmlFor="nome">Scegli il cantiere a cui associare il task:</Form.Label>
              <Form.Select
                id="nome"
                name="cantiereId"
                value={task.cantiereId}
                onChange={handleChange}
                disabled={!!task.id}
              >
                <option disabled value="">
                  Seleziona Nome
                </option>
                {cantieri.map((cantiere, idx) => (
                  <option key={idx} value={cantiere.id}>
                    {cantiere.nome}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : type === "evento" ? (
            <Form.Group className="mb-3">
              <Form.Label htmlFor="titolo">Scegli l'evento a cui associare il task:</Form.Label>
              <Form.Select
                id="titolo"
                name="eventoId"
                value={task.eventoId}
                onChange={handleChange}
                disabled={!!task.id}
              >
                <option disabled value="">
                  Seleziona Titolo
                </option>
                {eventi.map((evento, idx) => (
                  <option key={idx} value={evento.id}>
                    {evento.titolo}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : null}

          <Row>
            {/* Titolo (obbligatorio) */}
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <h5 className="m-0">Titolo *</h5>
                </Form.Label>
                <InputText
                  name="titolo"
                  value={task.titolo}
                  onChange={(e) => {
                    handleChange(e);
                    setErrors((prev) => ({ ...prev, titolo: false }));
                  }}
                  placeholder="Titolo"
                  style={{
                    height: "3rem",
                    border: errors.titolo ? "1px solid red" : undefined,
                  }}
                  className="w-100 pad-02"
                />
              </Form.Group>
            </Col>

            {/* Assegnato a */}
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <h5 className="m-0">Assegnato a</h5>
                </Form.Label>
                <Dropdown
                  style={{ height: "3rem" }}
                  className="w-100"
                  value={task.assegnatoA}
                  options={dipendenti
                    .filter((dipendente) =>
                      `${dipendente.nome} ${dipendente.cognome}`
                        .toLowerCase()
                        .includes(searchTextDipendente.toLowerCase())
                    )
                    .map((dipendente) => ({
                      label: `${dipendente.nome} ${dipendente.cognome}`,
                      value: dipendente.id,
                    }))}
                  onChange={(e) => setTask((prev) => ({ ...prev, assegnatoA: e.value }))}
                  onInput={(e) => setSearchTextDipendente((e.target as HTMLInputElement).value)}
                  placeholder="Seleziona dipendente"
                  editable
                />
              </Form.Group>
            </Col>

            {/* Stato (obbligatorio) */}
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <h5 className="m-0">Stato *</h5>
                </Form.Label>
                <Dropdown
                  style={{
                    height: "3rem",
                  }}
                  className="w-100"
                  value={task.stato}
                  options={[
                    { label: "Da fare", value: "da_fare" },
                    { label: "In corso", value: "in_corso" },
                    { label: "Completato", value: "completato" },
                    { label: "Annullato", value: "annullato" },
                  ].filter((option) => option.label.toLowerCase().includes(searchTextStato.toLowerCase()))}
                  onChange={(e) => {
                    setTask((prev) => ({ ...prev, stato: e.value }));
                    setErrors((prev) => ({ ...prev, stato: false }));
                  }}
                  onInput={(e) => setSearchTextStato((e.target as HTMLInputElement).value)}
                  placeholder="Seleziona stato"
                  editable
                />
              </Form.Group>
            </Col>

            {/* Data Inizio */}
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <h5 className="m-0">Data Inizio *</h5>
                </Form.Label>
                <div>
                  <DatePicker
                    selected={task.dataInizio ? new Date(task.dataInizio) : null}
                    onChange={(date) => {
                      handleDateChange("dataInizio", date);
                      setErrors((prev) => ({ ...prev, dataInizio: false }));
                    }}
                    placeholderText="Seleziona data inizio"
                    className="w-100 input-crea-edit cursor-pointer h-3r"
                    dateFormat="dd/MM/yyyy"
                    locale="it"
                  />
                </div>
              </Form.Group>
            </Col>

            {/* Scadenza (obbligatoria) */}
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <h5 className="m-0">Scadenza *</h5>
                </Form.Label>
                <div>
                  <DatePicker
                    selected={task.scadenza ? new Date(task.scadenza) : null}
                    onChange={(date) => {
                      handleDateChange("scadenza", date);
                      setErrors((prev) => ({ ...prev, scadenza: false }));
                    }}
                    placeholderText="Seleziona scadenza"
                    className="w-100 input-crea-edit cursor-pointer h-3r"
                    dateFormat="dd/MM/yyyy"
                    locale="it"
                  />
                </div>
              </Form.Group>
            </Col>

            {/* Data Fine Effettiva */}
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <h5 className="m-0">Data Fine Effettiva</h5>
                </Form.Label>
                <DatePicker
                  selected={task.dataFineEffettiva ? new Date(task.dataFineEffettiva) : null}
                  onChange={(date) => handleDateChange("dataFineEffettiva", date)}
                  placeholderText="Seleziona data fine effettiva"
                  className="w-100 input-crea-edit cursor-pointer h-3r"
                  dateFormat="dd/MM/yyyy"
                  locale="it"
                />
              </Form.Group>
            </Col>

            {/* Promemoria */}
            <Col xs={12}>
              <Form.Group className="mb-3 d-flex align-items-center">
                <Form.Label className="m-0">
                  <h5 className="m-0">Promemoria</h5>
                </Form.Label>
                <Form.Check
                  type="switch"
                  id="promemoria-switch"
                  label=""
                  name="switchPromemoria"
                  checked={switchPromemoria}
                  onChange={handleChange}
                  className="ms-3"
                />
                {switchPromemoria && (
                  <div className="ms-3">
                    {task.promemoria === "TRE_GIORNI_PRIMA" ? (
                      <p className="m-0">Tre Giorni Prima</p>
                    ) : task.promemoria === "UN_GIORNO_PRIMA" ? (
                      <p className="m-0">Un giorno prima</p>
                    ) : task.promemoria === "TUTTI_I_GIORNI" ? (
                      <p className="m-0">tutti i giorni</p>
                    ) : task.promemoria === "SCELTA_LIBERA" ? (
                      <p className="m-0">promemoria fissato per il: {task.dataPromemoriaPersonalizzata}</p>
                    ) : (
                      ""
                    )}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Descrizione */}
          <Form.Group className="mb-3 mt-3">
            <Form.Label>
              <h5 className="m-0">Descrizione</h5>
            </Form.Label>
            <InputTextarea
              name="descrizione"
              value={task.descrizione}
              onChange={handleChange}
              placeholder="Descrizione"
              rows={5}
              className="w-100 bg-light"
            />
          </Form.Group>

          <div className="d-flex justify-content-between align-items-center mt-5">
            <div
              className={
                iscreate
                  ? "d-flex justify-content-between align-items-center d-none"
                  : "d-flex justify-content-between align-items-center"
              }
            >
              <FaTrashAlt className="icon-action" title="Elimina" style={{ cursor: "pointer", color: "red" }} />
              <p
                className={!iscreate ? "scritta-elimina" : "d-none"}
                style={{ cursor: "pointer", color: "red" }}
                onClick={() => deleteTask(task)}
              >
                Elimina
              </p>
            </div>

            <Button className="button d-flex px-3 ms-auto" onClick={handleSubmit}>
              <i className="pi pi-save me-2"></i>
              <p className="p-0 m-0">Salva</p>
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Promemoria Options Modal */}
      <Dialog
        visible={isPromemoriaModalOpen}
        header="Seleziona quando vuoi ricevere il promemoria"
        style={{ width: "20vw" }}
        onHide={handlePromemoriaModalClose}
      >
        <div>
          <Button onClick={() => handlePromemoriaOptionSelect("UN_GIORNO_PRIMA")} className="button mb-2 w-100">
            Un giorno prima
          </Button>
          <Button onClick={() => handlePromemoriaOptionSelect("TRE_GIORNI_PRIMA")} className="button mb-2 w-100">
            Tre giorni prima
          </Button>
          <Button onClick={() => handlePromemoriaOptionSelect("TUTTI_I_GIORNI")} className="button mb-2 w-100">
            Ogni giorno finché non è completato
          </Button>
          <Button
            onClick={() => {
              handlePromemoriaOptionSelect("scelta_libera");
              setDatePickerVisible(true);
            }}
            className="button mb-2 d-flex align-items-center justify-content-center w-100"
          >
            <FaCalendarAlt className="calendar-icon" />
            <span className="ms-2">Personalizzato</span>
          </Button>
        </div>
      </Dialog>

      {/* DatePicker Modal per data personalizzata */}
      <Dialog
        visible={datePickerVisible}
        header="Seleziona una data"
        style={{ width: "300px" }}
        contentStyle={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
        onHide={() => setDatePickerVisible(false)}
      >
        <DatePicker
          inline
          selected={task.dataPromemoriaPersonalizzata ? new Date(task.dataPromemoriaPersonalizzata) : null}
          onChange={(date) => {
            handleDateChange("dataPromemoriaPersonalizzata", date);
            setDatePickerVisible(false);
            setPromemoriaModalOpen(false);
          }}
          dateFormat="dd/MM/yyyy"
          locale="it"
          minDate={new Date()}
        />
        <Button className="button mt-3 w-100" onClick={() => setDatePickerVisible(false)}>
          Conferma
        </Button>
      </Dialog>

      {/* DeleteModal per Task */}
      {deleteToTask && (
        <DeleteModal
          type="task"
          idObj={deleteToTask.id}
          matchIdentifier={deleteToTask.titolo}
          show={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onDeleteSuccess={() => {
            handleCloseModal();
          }}
        />
      )}
    </>
  );
};

export default ModalFormTaskSingolo;
